import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { useUser } from '../state/UserContext';

/** Concrete practice milestones (see the Omnist Development Plan's "Progress and motivation") --
 * primary UI leads with these instead of a spiritual-authority-sounding rank title. A milestone
 * already reached shows as "X practice days"; short of the first one, an honest running count. */
function formatPracticeMilestoneLabel(totalPracticeDays: number, milestone: number): string {
  if (milestone > 0) return `${milestone} practice days`;
  if (totalPracticeDays > 0) return `${totalPracticeDays} practice day${totalPracticeDays === 1 ? '' : 's'}`;
  return 'Just beginning';
}

const NAV_ITEMS = [
  { to: '/home', labelKey: 'nav.home', icon: '🏠' },
  { to: '/practices', labelKey: 'nav.practices', icon: '🧘' },
  { to: '/calendar', labelKey: 'nav.calendar', icon: '📅' },
  { to: '/library', labelKey: 'nav.library', icon: '📚' },
  { to: '/journal', labelKey: 'nav.journal', icon: '📓' },
  { to: '/leaderboard', labelKey: 'nav.leaderboard', icon: '🏆' },
  { to: '/settings', labelKey: 'nav.settings', icon: '⚙️' },
] as const;

/**
 * A slim top bar (hamburger + wordmark) plus an overlay drawer that slides in from the left --
 * same interaction as theclowncr.vip and Ember's site nav, restyled to Omnist's own theme tokens
 * rather than their fixed dark palette. One nav pattern at every viewport size.
 */
export function NavBar() {
  const { me } = useUser();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const openButtonRef = useRef<HTMLButtonElement>(null);

  // Auto-close on navigation.
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return;
    closeButtonRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const close = () => {
    setIsOpen(false);
    openButtonRef.current?.focus();
  };

  return (
    <>
      <header className="top-bar">
        {me && (
          <span
            className="header-streak"
            title={`${me.currentStreak}-day streak · ${formatPracticeMilestoneLabel(me.totalPracticeDays, me.practiceMilestone)}`}
          >
            <span aria-hidden="true">🔥</span> {me.currentStreak}
            <span className="header-rank">{formatPracticeMilestoneLabel(me.totalPracticeDays, me.practiceMilestone)}</span>
          </span>
        )}
        <button
          ref={openButtonRef}
          type="button"
          className="hamburger-btn"
          aria-label={t('nav.openMenu')}
          aria-expanded={isOpen}
          aria-controls="nav-drawer"
          onClick={() => setIsOpen(true)}
        >
          <span />
          <span />
          <span />
        </button>
        <span className="top-bar-brand">
          <span className="logo-badge">
            <img src="/logo-face.png" alt="" className="logo-face" />
          </span>
          <span className="wordmark">Omnist</span>
        </span>
      </header>

      <div
        className={`drawer-overlay${isOpen ? ' open' : ''}`}
        onClick={close}
        aria-hidden={!isOpen}
      />

      <nav
        id="nav-drawer"
        className={`nav-drawer${isOpen ? ' open' : ''}`}
        aria-label="Primary"
        aria-hidden={!isOpen}
      >
        <div className="nav-drawer-header">
          <div className="nav-drawer-brand">
            <span className="logo-badge">
              <img src="/logo-face.png" alt="" className="logo-face" />
            </span>
            <div>
              <div className="nav-drawer-title">{t('nav.menu')}</div>
              <div className="nav-drawer-subtitle wordmark">Omnist</div>
            </div>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="drawer-close-btn"
            aria-label={t('nav.closeMenu')}
            onClick={close}
          >
            ✕
          </button>
        </div>

        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
          >
            <span className="nav-icon" aria-hidden="true">{item.icon}</span>
            <span>{t(item.labelKey)}</span>
          </NavLink>
        ))}

        {me?.isAdmin && (
          <NavLink to="/admin" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            <span className="nav-icon" aria-hidden="true">🛠️</span>
            <span>{t('nav.admin')}</span>
          </NavLink>
        )}
      </nav>
    </>
  );
}
